import React from 'react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { fmtDate, fmtAmt, STATUS_COLORS } from './helpers';

const OrderCard = ({ order, isPending, activeTab, isExpanded, onToggleExpand, onOpenDesignImage, processing, onApprove, onReject }) => {
  const isSO = activeTab === 'so';
  const number = isSO ? order.so_number : order.po_number;
  const date = isSO ? order.so_date : order.po_date;
  const name = isSO ? order.customer_name : order.vendor_name;
  const nameLabel = isSO ? 'Customer' : 'Vendor';
  const totalQty = (order.items || []).reduce((s, i) =>
    s + (i.variants || []).reduce((vs, v) => vs + (v.qty_ordered || 0), 0), 0);
  const totalItems = (order.items || []).length;

  return (
    <Card key={order.id} className="border-l-4 transition-shadow hover:shadow-md" style={{
      borderLeftColor: isPending ? '#f59e0b' : '#ef4444'
    }} data-testid={`approval-card-${order.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-2">
              <span className="font-bold text-base font-mono text-gray-900" data-testid={`order-number-${order.id}`}>{number}</span>
              <Badge className={STATUS_COLORS[order.approval_status]}>
                {order.approval_status === 'pending_approval' ? 'Pending Approval' : 'Rejected'}
              </Badge>
              <span className="text-xs text-gray-500">{fmtDate(date)}</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-1 text-sm">
              <div>
                <span className="text-gray-500 text-xs">{nameLabel}</span>
                <p className="font-semibold text-base truncate">{name || 'N/A'}</p>
              </div>
              <div>
                <span className="text-gray-500 text-xs">Items</span>
                <p className="font-medium">{totalItems} designs</p>
              </div>
              <div>
                <span className="text-gray-500 text-xs">Qty</span>
                <p className="font-medium">{totalQty} pcs</p>
              </div>
              <div>
                <span className="text-gray-500 text-xs">Amount</span>
                <p className="font-medium text-emerald-700">{fmtAmt(order.total_amount)}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 mt-2 text-xs text-gray-500 flex-wrap">
              <span>Created by: <strong className="text-gray-700">{order.created_by_name}</strong></span>
              {order.agent_name && <span className="text-violet-600">Agent: <strong>{order.agent_name}</strong></span>}
              {order.distributor_name && <span className="text-orange-600">Dist: <strong>{order.distributor_name}</strong></span>}
              {order.notes && <span className="italic truncate max-w-[200px]">Note: {order.notes}</span>}
            </div>
            {order.rejection_reason && (
              <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                <strong>Rejection Reason:</strong> {order.rejection_reason}
              </div>
            )}
            {/* Expandable item details */}
            <button
              onClick={onToggleExpand}
              className="mt-3 flex items-center gap-1.5 text-xs font-medium text-gray-600 hover:text-gray-900 transition-colors"
              data-testid={`expand-btn-${order.id}`}
            >
              <svg className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
              </svg>
              {totalItems} design{totalItems !== 1 ? 's' : ''} &middot; {totalQty} pcs
            </button>
            {isExpanded && (
              <div className="mt-2 border rounded-lg overflow-hidden" data-testid={`items-detail-${order.id}`}>
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 text-gray-600">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium">Design</th>
                      <th className="px-3 py-2 text-left font-medium">Variants</th>
                      <th className="px-3 py-2 text-right font-medium">Qty</th>
                      <th className="px-3 py-2 text-right font-medium">Rate</th>
                      <th className="px-3 py-2 text-right font-medium">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {(order.items || []).map((item, idx) => {
                      const variants = item.variants || [];
                      const itemQty = variants.reduce((s, v) => s + (v.qty_ordered || 0), 0);
                      const rate = variants.length > 0 ? (variants[0].rate || variants[0].cost_per_piece || 0) : 0;
                      const itemAmt = variants.reduce((s, v) => s + (v.qty_ordered || 0) * (v.rate || v.cost_per_piece || 0), 0);
                      return (
                        <tr key={idx} className="hover:bg-gray-50">
                          <td className="px-3 py-2">
                            <button
                              onClick={(e) => onOpenDesignImage(item.design_code, e)}
                              className="font-mono font-semibold text-blue-700 hover:text-blue-900 hover:underline cursor-pointer"
                              data-testid={`design-img-btn-${order.id}-${idx}`}
                            >
                              #{item.design_code}
                            </button>
                            {item.brand_id && <span className="ml-1.5 text-gray-400 text-[10px]">{item.fabric || ''}</span>}
                          </td>
                          <td className="px-3 py-2 text-gray-500">
                            {variants.slice(0, 3).map((v, vi) => (
                              <span key={vi} className="inline-block mr-1">
                                {v.color}/{v.size}
                              </span>
                            ))}
                            {variants.length > 3 && <span className="text-gray-400">+{variants.length - 3}</span>}
                          </td>
                          <td className="px-3 py-2 text-right font-medium">{itemQty}</td>
                          <td className="px-3 py-2 text-right">{fmtAmt(rate)}</td>
                          <td className="px-3 py-2 text-right font-medium text-emerald-700">{fmtAmt(itemAmt)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot className="bg-gray-50 font-semibold text-gray-800">
                    <tr>
                      <td className="px-3 py-2">{totalItems} designs</td>
                      <td className="px-3 py-2"></td>
                      <td className="px-3 py-2 text-right">{totalQty}</td>
                      <td className="px-3 py-2"></td>
                      <td className="px-3 py-2 text-right text-emerald-700">{fmtAmt(order.total_amount)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
          {/* Action buttons */}
          {isPending && (
            <div className="flex flex-col gap-2 shrink-0">
              <Button
                size="sm"
                className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5"
                disabled={processing === order.id}
                onClick={() => onApprove(number)}
                data-testid={`approve-btn-${order.id}`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
                Approve
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="border-red-300 text-red-600 hover:bg-red-50 gap-1.5"
                disabled={processing === order.id}
                onClick={() => onReject(number)}
                data-testid={`reject-btn-${order.id}`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
                Reject
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default OrderCard;
